import { gql, useQuery } from '@apollo/client'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { useLocale, useTranslate } from 'react-admin'
import { Link } from 'react-router-dom'
import { BoatvertizerAccount } from './BoatvertizerAccount'
import { ViewsChart } from './ViewsChart'

/**
 * What Boatvertizer reports: per advert, where it is online and how it does —
 * views and inquiries over the period Boatvertizer evaluates (90 days in
 * August 2026, a year since October) — and the daily views of all adverts
 * together. Data in boatvertizer_*, fetched by the feed service.
 */
const STATS = gql`
  query BoatvertizerStats {
    account: boatvertizer_account_by_pk(id: 1) {
      username
      saved_at
      last_fetch_at
      last_fetch_ok
      last_fetch_message
    }
    listings: boatvertizer_listings(order_by: { title: asc }) {
      listing_id
      title
      public_url
      status
      portals
      captured_at
      boat {
        id
        name
      }
      windows(order_by: { window_end: desc }, limit: 1) {
        window_start
        window_end
        detailviews
        resultviews
        ctr
        inquiries
      }
    }
    daily: boatvertizer_stats_daily(order_by: { date: asc }) {
      date
      detailviews
    }
  }
`

interface Portal {
  name: string
  status: string
  info?: string
  url?: string | null
}

const PORTAL_TONE: Record<string, 'success' | 'warning' | 'default'> = {
  active: 'success',
  pending: 'warning',
}

const Tile = ({
  label,
  value,
  sub,
  testId,
}: {
  label: string
  value: string
  sub?: string
  testId?: string
}) => (
  <Card variant='outlined' sx={{ height: '100%' }} data-testid={testId}>
    <CardContent>
      <Typography variant='body2' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='h4' component='div' sx={{ my: 0.5 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant='body2' color='text.secondary'>
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
)

export const BoatvertizerStats = () => {
  const translate = useTranslate()
  const locale = useLocale()
  const { data, error, refetch } = useQuery(STATS)
  const number = useMemo(() => new Intl.NumberFormat(locale), [locale])
  const date = useMemo(() => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }), [locale])

  const daily = useMemo(() => {
    const sums = new Map<string, number>()
    for (const row of data?.daily ?? [])
      sums.set(row.date, (sums.get(row.date) ?? 0) + (row.detailviews ?? 0))
    return Array.from(sums, ([d, value]) => ({ date: d, value }))
  }, [data])

  if (error) return <Alert severity='error'>{error.message}</Alert>
  if (!data) return null

  // Only what the last fetch reported. Adverts Boatvertizer no longer has, and
  // figures for an earlier period, would otherwise mix into the totals.
  const captured: string = data.listings.reduce(
    (latest: string, l: any) => (l.captured_at > latest ? l.captured_at : latest),
    ''
  )
  const current = data.listings.filter((l: any) => l.captured_at === captured)
  const gone = data.listings.length - current.length
  const windowEnd: string = current.reduce(
    (latest: string, l: any) =>
      (l.windows[0]?.window_end ?? '') > latest ? l.windows[0].window_end : latest,
    ''
  )
  const windowFor = (l: any) => (l.windows[0]?.window_end === windowEnd ? l.windows[0] : null)
  const listings = [...current].sort(
    (a: any, b: any) => (windowFor(b)?.detailviews ?? 0) - (windowFor(a)?.detailviews ?? 0)
  )
  const account = <BoatvertizerAccount account={data.account} onChanged={() => refetch()} />
  if (listings.length === 0) {
    return (
      <Card variant='outlined' data-testid='bv-stats'>
        <CardContent>
          <Typography variant='h6' sx={{ mb: 1 }}>
            {translate('feed.bv.title')}
          </Typography>
          {account}
          <Typography color='text.secondary'>{translate('feed.bv.empty')}</Typography>
        </CardContent>
      </Card>
    )
  }

  const views = listings.reduce((sum: number, l: any) => sum + (windowFor(l)?.detailviews ?? 0), 0)
  const inquiries = listings.reduce(
    (sum: number, l: any) => sum + (windowFor(l)?.inquiries ?? 0),
    0
  )
  const online = listings.filter((l: any) => l.status === 'active').length
  const windowOf = listings.map(windowFor).find(Boolean)
  const period = windowOf
    ? `${date.format(new Date(windowOf.window_start))} – ${date.format(new Date(windowOf.window_end))}`
    : ''

  return (
    <Card variant='outlined' data-testid='bv-stats'>
      <CardContent>
        <Stack direction='row' spacing={1} sx={{ mb: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant='h6'>{translate('feed.bv.title')}</Typography>
          <Typography variant='body2' color='text.secondary'>
            {translate('feed.bv.as_of', { date: date.format(new Date(captured)) })}
          </Typography>
        </Stack>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {translate('feed.bv.hint')}
        </Typography>
        {account}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Tile
              testId='bv-views'
              label={translate('feed.bv.views')}
              value={number.format(views)}
              sub={period}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Tile
              testId='bv-inquiries'
              label={translate('feed.bv.inquiries')}
              value={number.format(inquiries)}
              sub={period}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Tile
              label={translate('feed.bv.online')}
              value={`${online} / ${listings.length}`}
              sub={translate('feed.bv.online_sub')}
            />
          </Grid>
        </Grid>

        <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
          {translate('feed.bv.chart')}
        </Typography>
        <ViewsChart points={daily} label={translate('feed.bv.chart')} />

        <Box sx={{ overflowX: 'auto', mt: 2 }}>
          <Table size='small' data-testid='bv-listings'>
            <TableHead>
              <TableRow>
                <TableCell>{translate('feed.bv.listing')}</TableCell>
                <TableCell>{translate('feed.bv.portals')}</TableCell>
                <TableCell align='right'>{translate('feed.bv.views_short')}</TableCell>
                <TableCell align='right'>{translate('feed.bv.inquiries')}</TableCell>
                <TableCell align='right'>{translate('feed.bv.ctr')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listings.map((l: any) => {
                const w = windowFor(l)
                const portals: Portal[] = l.portals ?? []
                return (
                  <TableRow key={l.listing_id}>
                    <TableCell>
                      {l.public_url ? (
                        <MuiLink href={l.public_url} target='_blank' rel='noopener'>
                          {l.title || `#${l.listing_id}`}
                        </MuiLink>
                      ) : (
                        l.title || `#${l.listing_id}`
                      )}
                      <Typography variant='caption' component='div' color='text.secondary'>
                        {l.boat ? (
                          <MuiLink component={Link} to={`/boats/${l.boat.id}/show`}>
                            {translate('feed.bv.crm_boat', { name: l.boat.name })}
                          </MuiLink>
                        ) : (
                          translate('feed.bv.not_matched')
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction='row' sx={{ gap: 0.5, flexWrap: 'wrap' }}>
                        {portals.map((p) => (
                          <Tooltip key={p.name} title={p.info ?? p.status}>
                            <Chip
                              size='small'
                              variant='outlined'
                              color={PORTAL_TONE[p.status] ?? 'default'}
                              label={`${p.name}: ${p.info ?? p.status}`}
                              component={p.url ? 'a' : 'div'}
                              href={p.url ?? undefined}
                              target={p.url ? '_blank' : undefined}
                              clickable={!!p.url}
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {w ? number.format(w.detailviews ?? 0) : '—'}
                    </TableCell>
                    <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {w ? number.format(w.inquiries ?? 0) : '—'}
                    </TableCell>
                    <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {w?.ctr != null ? `${number.format(Number(w.ctr))} %` : '—'}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>
        {gone > 0 && (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }} data-testid='bv-gone'>
            {translate('feed.bv.gone', { smart_count: gone })}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

export default BoatvertizerStats
